import type { ReactElement, ReactNode } from 'react';

import { RiArrowDownLine, RiArrowUpLine } from '@remixicon/react';

import { Box, Flex, Text } from '@spotify-internal/encore-web';

import type {
  PreviewField,
  ToolConfirmationRenderProps,
} from '../../extensions';
import {
  ConfirmationConfigGrid,
  ResourceConfirmationCard,
} from '../ResourceConfirmationCard';
import { useResolveResourceName } from '../ToolConfirmationMessage';
import {
  type ExperimentMetric,
  type ExperimentTreatment,
  formatMinimumDetectableEffect,
  formatNonInferiorityMargin,
  getExperimentMetricDirectionText,
} from './formatters';

import styles from './ExperimentConfirmation.module.css';

type RootProps = ToolConfirmationRenderProps & {
  children?: ReactNode;
  icon?: ReactNode;
  resourceHints?: string[];
  title: ReactNode;
};

const Root = ({
  children,
  icon,
  resourceHints,
  title,
  ...confirmationProps
}: RootProps): ReactElement => (
  <ResourceConfirmationCard
    {...confirmationProps}
    icon={icon}
    resourceHints={resourceHints}
    title={title}
  >
    {children}
  </ResourceConfirmationCard>
);

const Config = ({
  input,
  previewFields,
}: {
  input: Record<string, unknown>;
  previewFields: PreviewField[];
}): ReactElement => (
  <ConfirmationConfigGrid input={input} previewFields={previewFields} />
);

const Treatments = ({ children }: { children: ReactNode }): ReactElement => (
  <Flex direction="column" gap="tighter-2">
    <Text variant="bodySmallBold">Treatments</Text>
    <Flex wrap="wrap" gap="tighter-2">
      {children}
    </Flex>
  </Flex>
);

const Treatment = ({
  children,
  index,
  treatment,
  weight,
}: {
  children?: ReactNode;
  index: number;
  treatment: ExperimentTreatment;
  weight?: ReactNode;
}): ReactElement => {
  const role = index === 0 ? 'Control' : 'Treatment';
  const name =
    treatment.displayName || treatment.name || treatment.variant || role;

  return (
    <Box
      variant="elevated"
      borderRadius="smaller"
      padding="tighter-2"
      className={styles.card}
    >
      <Flex alignItems="start" justifyContent="space-between" gap="tighter">
        <Flex direction="column">
          <Text variant="bodySmall" semanticColor="textSubdued">
            {role}
          </Text>
          <Text variant="bodySmallBold">{name}</Text>
          {children}
        </Flex>
        {weight}
      </Flex>
    </Box>
  );
};

const TreatmentWeight = ({ weight }: { weight: ReactNode }): ReactElement => (
  <Text variant="bodySmall" semanticColor="textSubdued">
    {weight}
  </Text>
);

const MetricCard = ({ metric }: { metric: ExperimentMetric }): ReactElement => {
  const resolve = useResolveResourceName();
  const directionText = getExperimentMetricDirectionText(
    metric.direction,
    metric.isGuardrail,
  );
  const mdeText = formatMinimumDetectableEffect(metric.mde);
  const nimText = formatNonInferiorityMargin(metric.nim);

  return (
    <Box variant="elevated" borderRadius="smaller" padding="tighter-2">
      <Flex alignItems="center" justifyContent="space-between" gap="tighter">
        <Text variant="bodySmallBold">{resolve(metric.metric)}</Text>
        <Flex alignItems="center" gap="tighter-2">
          {metric.direction === 'DECREASE' ? (
            <RiArrowDownLine
              aria-hidden="true"
              size={14}
              className={styles.directionIcon}
            />
          ) : (
            <RiArrowUpLine
              aria-hidden="true"
              size={14}
              className={styles.directionIcon}
            />
          )}
          <Text variant="bodySmall" semanticColor="textSubdued">
            {directionText}
          </Text>
          {mdeText && (
            <Text variant="bodySmall" semanticColor="textSubdued">
              {mdeText}
            </Text>
          )}
          {nimText && (
            <Text variant="bodySmall" semanticColor="textSubdued">
              {nimText}
            </Text>
          )}
        </Flex>
      </Flex>
    </Box>
  );
};

const Metrics = ({
  metrics,
}: {
  metrics: ExperimentMetric[];
}): ReactElement => {
  const successMetrics = metrics.filter(metric => !metric.isGuardrail);
  const guardrailMetrics = metrics.filter(metric => metric.isGuardrail);

  return (
    <Flex direction="column" gap="tighter-2">
      <Text variant="bodySmallBold">Metrics</Text>
      {successMetrics.length > 0 && (
        <Flex direction="column" gap="tighter-2">
          <Text variant="bodySmall" semanticColor="textSubdued">
            Success
          </Text>
          {successMetrics.map(metric => (
            <MetricCard key={metric.metric} metric={metric} />
          ))}
        </Flex>
      )}
      {guardrailMetrics.length > 0 && (
        <Flex direction="column" gap="tighter-2">
          <Text variant="bodySmall" semanticColor="textSubdued">
            Guardrail
          </Text>
          {guardrailMetrics.map(metric => (
            <MetricCard key={metric.metric} metric={metric} />
          ))}
        </Flex>
      )}
    </Flex>
  );
};

export const ExperimentConfirmation = {
  Config,
  Metrics,
  Root,
  Treatment,
  TreatmentWeight,
  Treatments,
};
